import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FiCalendar, 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiDollarSign,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiEye,
  FiUsers
} from 'react-icons/fi';

// API configuration
const API_URL = process.env.REACT_APP_API_URL || '/api';

const EventRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchEventRequests();
  }, [currentPage, statusFilter]);

  const fetchEventRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const response = await fetch(
        `${API_URL}/admin/management/event-requests?page=${currentPage}&limit=10${statusParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch event requests');
      }

      const data = await response.json();
      if (data.success) {
        setRequests(data.data.requests);
        setTotalPages(data.data.pagination.total);
      } else {
        throw new Error(data.message || 'Failed to fetch event requests');
      }
    } catch (error) {
      console.error('Error fetching event requests:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestDetails = async (requestId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_URL}/admin/management/event-requests/${requestId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch request details');
      }

      const data = await response.json();
      if (data.success) {
        setSelectedRequest(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch request details');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      setError(error.message);
    }
  };

  const handleApproveRequest = async (requestId, adminNotes = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_URL}/admin/management/event-requests/${requestId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ adminNotes })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve request');
      }

      const data = await response.json();
      if (data.success) {
        alert('Event request approved and sent to vendors!');
        fetchEventRequests();
        setSelectedRequest(null);
      } else {
        throw new Error(data.message || 'Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error approving request: ' + error.message);
    }
  };

  const handleRejectRequest = async (requestId, adminNotes = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_URL}/admin/management/event-requests/${requestId}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ adminNotes })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reject request');
      }

      const data = await response.json();
      if (data.success) {
        alert('Event request rejected');
        fetchEventRequests();
        setSelectedRequest(null);
      } else {
        throw new Error(data.message || 'Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rejecting request: ' + error.message);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FiClock style={{ color: '#f39c12' }} />;
      case 'approved':
        return <FiCheckCircle style={{ color: '#27ae60' }} />;
      case 'rejected':
        return <FiXCircle style={{ color: '#e74c3c' }} />;
      default:
        return <FiClock style={{ color: '#95a5a6' }} />;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading event requests...</LoadingSpinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Event Requests Management</Title>
        <FilterContainer>
          <FilterSelect 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Requests</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </FilterSelect>
        </FilterContainer>
      </Header>

      <Content>
        <RequestsList>
          {requests.length === 0 ? (
            <EmptyState>No event requests found</EmptyState>
          ) : (
            requests.map((request) => (
              <RequestCard key={request._id}>
                <RequestHeader>
                  <CustomerInfo>
                    <CustomerName>
                      <FiUser /> {request.customerName}
                    </CustomerName>
                    <EventDetails>
                      <DetailItem>
                        <FiCalendar /> {request.eventType}
                        {request.eventSubType && ` - ${request.eventSubType}`}
                      </DetailItem>
                      <DetailItem>
                        <FiCalendar /> {formatDate(request.eventDate)}
                      </DetailItem>
                      <DetailItem>
                        <FiUsers /> {request.guestCount} guests
                      </DetailItem>
                    </EventDetails>
                  </CustomerInfo>
                  <StatusContainer>
                    <StatusBadge status={request.status}>
                      {getStatusIcon(request.status)}
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </StatusBadge>
                    <TotalCost>
                      <FiDollarSign /> ${request.totalCost?.toFixed(2) || '0.00'}
                    </TotalCost>
                  </StatusContainer>
                </RequestHeader>

                <ContactInfo>
                  <ContactItem>
                    <FiMail /> {request.customerEmail}
                  </ContactItem>
                  <ContactItem>
                    <FiPhone /> {request.customerPhone}
                  </ContactItem>
                  <ContactItem>
                    <FiMapPin /> {request.location}
                  </ContactItem>
                </ContactInfo>

                <PerksSection>
                  <PerksTitle>Selected Perks ({request.selectedPerks?.length || 0})</PerksTitle>
                  <PerksGrid>
                    {request.selectedPerks?.slice(0, 3).map((perk, index) => (
                      <PerkItem key={index}>
                        <PerkPrice>${perk.price?.toFixed(2)}</PerkPrice>
                        <PerkDescription>{perk.customMessage || 'Custom perk request'}</PerkDescription>
                      </PerkItem>
                    ))}
                    {request.selectedPerks?.length > 3 && (
                      <MorePerks>+{request.selectedPerks.length - 3} more</MorePerks>
                    )}
                  </PerksGrid>
                </PerksSection>

                <RequestActions>
                  <ViewButton onClick={() => fetchRequestDetails(request._id)}>
                    <FiEye /> View Details
                  </ViewButton>
                  {request.status === 'pending' && (
                    <>
                      <ApproveButton onClick={() => handleApproveRequest(request._id)}>
                        <FiCheckCircle /> Approve
                      </ApproveButton>
                      <RejectButton onClick={() => handleRejectRequest(request._id)}>
                        <FiXCircle /> Reject
                      </RejectButton>
                    </>
                  )}
                </RequestActions>
              </RequestCard>
            ))
          )}
        </RequestsList>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            <PageInfo>Page {currentPage} of {totalPages}</PageInfo>
            <PaginationButton 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationContainer>
        )}
      </Content>

      {/* Request Details Modal */}
      {selectedRequest && (
        <Modal onClick={() => setSelectedRequest(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Event Request Details</ModalTitle>
              <CloseButton onClick={() => setSelectedRequest(null)}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <DetailSection>
                <SectionTitle>Customer Information</SectionTitle>
                <DetailRow>
                  <DetailLabel>Name:</DetailLabel>
                  <DetailValue>{selectedRequest.customerName}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Email:</DetailLabel>
                  <DetailValue>{selectedRequest.customerEmail}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Phone:</DetailLabel>
                  <DetailValue>{selectedRequest.customerPhone}</DetailValue>
                </DetailRow>
              </DetailSection>

              <DetailSection>
                <SectionTitle>Event Information</SectionTitle>
                <DetailRow>
                  <DetailLabel>Event Type:</DetailLabel>
                  <DetailValue>{selectedRequest.eventType}</DetailValue>
                </DetailRow>
                {selectedRequest.eventSubType && (
                  <DetailRow>
                    <DetailLabel>Sub Type:</DetailLabel>
                    <DetailValue>{selectedRequest.eventSubType}</DetailValue>
                  </DetailRow>
                )}
                <DetailRow>
                  <DetailLabel>Date:</DetailLabel>
                  <DetailValue>{formatDate(selectedRequest.eventDate)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Guests:</DetailLabel>
                  <DetailValue>{selectedRequest.guestCount}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Location:</DetailLabel>
                  <DetailValue>{selectedRequest.location}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Budget:</DetailLabel>
                  <DetailValue>${selectedRequest.budget?.toFixed(2) || 'Not specified'}</DetailValue>
                </DetailRow>
              </DetailSection>

              <DetailSection>
                <SectionTitle>Selected Perks</SectionTitle>
                {selectedRequest.selectedPerks?.map((perk, index) => (
                  <PerkDetailCard key={index}>
                    <PerkDetailHeader>
                      <PerkDetailTitle>Perk #{index + 1}</PerkDetailTitle>
                      <PerkDetailPrice>${perk.price?.toFixed(2)}</PerkDetailPrice>
                    </PerkDetailHeader>
                    {perk.customMessage && (
                      <PerkDetailMessage>{perk.customMessage}</PerkDetailMessage>
                    )}
                    <PerkDetailActions>
                      <AcceptPerkButton 
                        onClick={() => handleApproveRequest(selectedRequest._id, `Approved perk ${index + 1}`)}
                      >
                        Accept Perk
                      </AcceptPerkButton>
                    </PerkDetailActions>
                  </PerkDetailCard>
                ))}
              </DetailSection>

              {selectedRequest.adminNotes && (
                <DetailSection>
                  <SectionTitle>Admin Notes</SectionTitle>
                  <AdminNotes>{selectedRequest.adminNotes}</AdminNotes>
                </DetailSection>
              )}
            </ModalBody>

            {selectedRequest.status === 'pending' && (
              <ModalActions>
                <ModalApproveButton 
                  onClick={() => handleApproveRequest(selectedRequest._id)}
                >
                  Approve Entire Request
                </ModalApproveButton>
                <ModalRejectButton 
                  onClick={() => handleRejectRequest(selectedRequest._id)}
                >
                  Reject Request
                </ModalRejectButton>
              </ModalActions>
            )}
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 0.9rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const RequestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RequestCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const CustomerInfo = styled.div`
  flex: 1;
`;

const CustomerName = styled.h3`
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EventDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #666;
  font-size: 0.9rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.status) {
      case 'pending': return '#fff3cd';
      case 'approved': return '#d4edda';
      case 'rejected': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#856404';
      case 'approved': return '#155724';
      case 'rejected': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

const TotalCost = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 600;
  color: #27ae60;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 5px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #666;
  font-size: 0.9rem;
`;

const PerksSection = styled.div`
  margin-bottom: 1rem;
`;

const PerksTitle = styled.h4`
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
`;

const PerksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
`;

const PerkItem = styled.div`
  padding: 0.5rem;
  background-color: #e8f5e8;
  border-radius: 5px;
  border: 1px solid #c3e6c3;
`;

const PerkPrice = styled.div`
  font-weight: 600;
  color: #27ae60;
  margin-bottom: 0.3rem;
`;

const PerkDescription = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const MorePerks = styled.div`
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 5px;
  border: 1px solid #dee2e6;
  text-align: center;
  color: #666;
  font-size: 0.8rem;
`;

const RequestActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 5px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.3s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ViewButton = styled(Button)`
  background-color: #3498db;
  color: white;

  &:hover {
    background-color: #2980b9;
  }
`;

const ApproveButton = styled(Button)`
  background-color: #27ae60;
  color: white;

  &:hover {
    background-color: #229954;
  }
`;

const RejectButton = styled(Button)`
  background-color: #e74c3c;
  color: white;

  &:hover {
    background-color: #c0392b;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PaginationButton = styled(Button)`
  background-color: #3498db;
  color: white;

  &:hover:not(:disabled) {
    background-color: #2980b9;
  }
`;

const PageInfo = styled.span`
  color: #666;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e74c3c;
  background-color: #f8d7da;
  border-radius: 5px;
  border: 1px solid #f5c6cb;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  background-color: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #dee2e6;
`;

// Modal Styles
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
`;

const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin: 0 0 1rem 0;
  border-bottom: 2px solid #3498db;
  padding-bottom: 0.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  min-width: 120px;
  color: #666;
`;

const DetailValue = styled.div`
  color: #2c3e50;
`;

const PerkDetailCard = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const PerkDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const PerkDetailTitle = styled.h4`
  margin: 0;
  color: #2c3e50;
`;

const PerkDetailPrice = styled.div`
  font-weight: 600;
  color: #27ae60;
`;

const PerkDetailMessage = styled.div`
  color: #666;
  margin-bottom: 1rem;
  font-style: italic;
`;

const PerkDetailActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const AcceptPerkButton = styled(Button)`
  background-color: #27ae60;
  color: white;

  &:hover {
    background-color: #229954;
  }
`;

const AdminNotes = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 5px;
  padding: 1rem;
  color: #856404;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
`;

const ModalApproveButton = styled(Button)`
  background-color: #27ae60;
  color: white;
  padding: 0.75rem 1.5rem;

  &:hover {
    background-color: #229954;
  }
`;

const ModalRejectButton = styled(Button)`
  background-color: #e74c3c;
  color: white;
  padding: 0.75rem 1.5rem;

  &:hover {
    background-color: #c0392b;
  }
`;

export default EventRequests;
